"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { useRouter } from "next/navigation";

import { UserAvatarMenu } from "@/components/nav/UserAvatarMenu";
import { ContactLink } from "@/components/contact/ContactLink";
import { Container } from "@/components/ui/Container";
import { dynamicWithRetry } from "@/lib/chunks/dynamicWithRetry";
import { useAuthUser } from "@/lib/auth/useAuthUser";

const AuthModal = dynamicWithRetry(
  () => import("@/components/auth/AuthModal").then((m) => m.AuthModal),
  { surface: "auth_quiz_nav" },
);

type Props = {
  user?: { name: string; email: string } | null;
};

export function QuizPageNav({ user: serverUser }: Props) {
  const user = useAuthUser(serverUser);
  const [authOpen, setAuthOpen] = useState(false);
  const [authHasOpened, setAuthHasOpened] = useState(false);
  const router = useRouter();

  return (
    <>
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-border-subtle bg-surface/80 backdrop-blur-md">
        <Container className="h-[var(--bsa-nav-h)] flex items-center justify-between">
          <Link
            href="/"
            aria-label="BestSafari.Africa home"
            className="inline-flex items-center rounded-[var(--radius-sm)] focus-ring"
          >
            <Image
              src="/logo.svg"
              alt="BestSafari.Africa"
              width={260}
              height={52}
              className="h-12 w-auto"
              priority
            />
          </Link>
          <div className="flex items-center gap-4">
            <ContactLink className="max-sm:hidden hover:text-primary transition-colors focus-ring rounded-[var(--radius-sm)] text-text-muted font-medium text-lg" />
            {user ? (
              <UserAvatarMenu user={user} />
            ) : (
              <button
                type="button"
                onClick={() => { setAuthOpen(true); setAuthHasOpened(true); }}
                className="hover:text-primary transition-colors focus-ring rounded-[var(--radius-sm)] text-text-muted font-medium text-lg"
              >
                Login / Sign up
              </button>
            )}
          </div>
        </Container>

        {!user && authHasOpened && (
          <AuthModal
            open={authOpen}
            onOpenChange={setAuthOpen}
            onSuccess={() => {
              setAuthOpen(false);
              router.refresh();
            }}
          />
        )}
      </nav>
      {/* Flow spacer — the nav above is fixed and takes no layout space; this
          reserves its height so page content isn't clipped underneath it. */}
      <div aria-hidden="true" className="h-[var(--bsa-nav-h)] shrink-0" />
    </>
  );
}
